import React, { useState, useEffect, useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "chart.js/auto";
import connectedIcon from "../../../../assets/images/connected.svg";
import disconnectedIcon from "../../../../assets/images/disconnected.svg";

import "./Production.css";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import ProductionChart from "./ProductionChart";
import { sysConfigApi } from "../../../../services/api/sysconfigService";
import { useTimeseriesSysconfig } from "../../hooks/useTimeseriesSysconfig";
import { listDevicesFromSysconfig } from "../../../devices/domain/deviceModel";
import {
  buildProductionFlowRows,
  deviceCardTotal,
} from "../Summary/cardFlowRows";
import CardFlowRows from "../Summary/CardFlowRows.jsx";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";
import {
  buildDemandChargePayload,
  sumDeviceAttributionForPeriod,
} from "../../utils/demandCharge";

export default function Production() {
  const { exportedProductionData } = useExportedData();
  
  const [deviceByType, setDeviceByType] = useState({
    Solar: [],
    Generator: [],
  });
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const token = sessionStorage.getItem("accessToken");
  const deviceType = "storage";
  const { timeSeriesFileName } = useNavigation();

  const accountname = sessionStorage.getItem("storageAccountName");

  const {
    storageAccount,
    microgridName,
    datasetName: resolvedDatasetName,
    loading: paramsLoading,
  } = useTimeseriesSysconfig();

  useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail") || sessionStorage.getItem("email");
    if (email) {
      setCustomer(email);
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);
  

  const StatsCardsSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="prod-sticky-cards">
        <div className="prod-card-container">
          <div className="prod-stats-container production-skeleton-card">
            <Skeleton width={120} height={24} style={{ marginBottom: "8px" }} />
            <Skeleton
              width={150}
              height={18}
              style={{ marginBottom: "20px" }}
            />
            <Skeleton width={80} height={16} style={{ marginBottom: "8px" }} />
            <div className="prod-cost-display">
              <Skeleton width={180} height={48} />
            </div>
            <div className="prod-stats" style={{ marginTop: "20px" }}>
              <div className="prod-stat-item">
                <Skeleton
                  width={80}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={60} height={20} />
              </div>
              <div className="prod-stat-item">
                <Skeleton
                  width={90}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={70} height={20} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );

  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        const data = await sysConfigApi({
          accountname,
          simulationName: microgridName,
          datasetName: resolvedDatasetName,
          token,
        });
        
        const mapping = { Solar: [], Generator: [] };
        const allDevices = listDevicesFromSysconfig(data);

        // Keep the normalized device, not just `raw`: it carries the
        // schema-derived `type` (from the sysconfig meter collection the device
        // came from) that the cards below key on. `raw` stays reachable at
        // `.raw` for the sysconfig-shaped fields.
        allDevices
          .filter((d) => d.type === "solar")
          .forEach((d) => mapping.Solar.push(d));

        allDevices
          .filter((d) => d.type === "generator")
          .forEach((d) => mapping.Generator.push(d));

        setDeviceByType(mapping);
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDeviceByType({ Solar: [], Generator: [] });
      } finally {
        setLoading(false);
      }
    };

    if (customer && token && resolvedDatasetName && !paramsLoading) {
      fetchDevices();
    }
  }, [customer, token, resolvedDatasetName, paramsLoading]);
  

  const allDevicesToRender = [
    ...(deviceByType.Solar || []),
    ...(deviceByType.Generator || []),
  ];

  
 const proddeviceNames = useMemo(() => {
    return {
      Solar: deviceByType?.Solar?.map((dev) => dev.name) || [],
      Generator: deviceByType?.Generator?.map((dev) => dev.name) || [],
    };
  }, [deviceByType?.Solar, deviceByType?.Generator]);
  
  /* ── Demand charge ────────────────────────────────────────────────────
   *
   * The same wiring as the Storage page, and for the same reason: a solar array
   * that shaves the site peak earns a share of the demand charge, and the
   * engine writes it per device ("Solar2040 Demand Attribution ($)" is
   * +2,719.57 over husky-2040-prod's year -- a benefit, so positive).
   *
   * Above the early returns below, which are conditional and would otherwise
   * change the hook order as the sysconfig fetch resolves. */
  const { hasDemandCharge, dailyDemand } = useDemandChargeData();

  /* The period the cards describe, as the ISO keys ProductionChart stamps on
   * every metrics entry -- not the "Month: January 2040" label beside them.
   * Every entry carries the same stamp, so the first is as good as any. */
  const period = useMemo(() => {
    const first = Array.isArray(exportedProductionData)
      ? exportedProductionData[0]
      : null;
    return {
      view: first?.periodView || "",
      selDate: first?.selDate || "",
      selMonth: first?.selMonth || "",
      selYear: first?.selYear || "",
    };
  }, [exportedProductionData]);

  const demandData = useMemo(
    () =>
      buildDemandChargePayload(
        hasDemandCharge,
        dailyDemand,
        period.view,
        period.selDate,
        period.selMonth,
        period.selYear,
      ),
    [hasDemandCharge, dailyDemand, period],
  );

  if (!loading && !deviceByType.Solar && !deviceByType.Generator) {
    return (
      <div className="storage-container">
        <div className="sticky-header">
          <h1>Storage</h1>
        </div>
        <div className="no-results">
          <p>No results available</p>
        </div>
      </div>
    );
  }

  const noDevices =
  !loading &&
  (!deviceByType.Solar || deviceByType.Solar.length === 0) &&
  (!deviceByType.Generator || deviceByType.Generator.length === 0);

if (noDevices) {
  return (
    <div className="storage-container">
      <div className="sticky-header">
        <h1>Production</h1>
      </div>
      <div className="no-results">
        <p>No devices available</p>
      </div>
    </div>
  );
}
  const handleLoadingStateChange = (loading) => {
    setIsChartLoading(loading);
  };

  const getMetricsByDevice = (deviceName) => {
    const found = exportedProductionData.find(
      (m) => m.deviceName === deviceName,
    );

    if (!found) {
      return {
        view: "",
        selectedPeriod: "",
        value: "0",
        maxPower: "0",
        totalEnergy: "0",
        avgSOC: "0",
        deviceName,
      };
    }
   
    return found;
  };

  const normalizeMetrics = (metrics) => {
  
    return {
      view: metrics.view,
      selectedPeriod: metrics.selectedPeriod,
      value: +metrics.totalProduction || 0,
      maxPower:
        metrics.type === "Solar"
          ? +metrics.maxSolar || 0
          : +metrics.maxGenerator || 0,
      /* UNCOERCED, like `generatedValue` below and for the same reason.
       * `+x || 0` turns an absent figure into a measured-looking 0, and this
       * one is read twice: it is the card's whole total, and it is the second
       * half of the Fuel row's derivation (fuel = generated value less
       * production cost). Coerced, a device whose 'Production Cost ($)'
       * column is missing shows a $0.00 total and a Fuel row equal to
       * everything it generated. The worker already reports the absence as
       * null (production.worker.js); this kept it from arriving. */
      totalEnergy: metrics.totalCost ?? null,
      /* Passed through UNCOERCED, same rule as `totalEnergy` above: the flow
       * row needs null to stay null so the cell shows an em-dash. */
      generatedValue: metrics.generatedValue ?? null,
      avgSOC: "N/A",
      deviceName: metrics.deviceName,
    };
  }

 
  return (
    <div className="storage-container">
      <div className="sticky-header">
        <h1>Production History</h1>

        
      </div>

      {isChartLoading || loading ? (
        <StatsCardsSkeleton />
      ) : (
        <>
          {allDevicesToRender.length > 0 && (
            <div className="sticky-cards-storage">
              <div
    className={`card-container-storage ${
      allDevicesToRender.length > 3 ? "scrollable-cards" : ""
  }`}
>
                {allDevicesToRender.map((device, deviceIndex) => {
                  const deviceName = device.name || "Unknown Device";
                  const isConnected = "connected";

                  // Schema-derived device type from listDevicesFromSysconfig
                  // ("solar" = the meter's `solar_arrays` collection). Distinct
                  // from the worker's `type`, which is only a CSV column token.
                  const type = device?.type;
                  // `type` alone collides when a customer has multiple solar
                  // (or multiple generator) devices; pair it with the array
                  // index so each card gets a stable, unique React key.
                  const cardKey = `${type}-${deviceIndex}`;

                  const metrics = getMetricsByDevice(deviceName);
                

                  const normalized = normalizeMetrics(metrics);
                  const {
                    view,
                    selectedPeriod,
                    value,
                    maxPower,
                    totalEnergy,
                    generatedValue,
                  } = normalized;

                  /* This device's share of the demand charge, over the same
                   * period the energy figures beside it cover. Daily-summary
                   * sign convention, passed through: a device that pulls the
                   * peak down reads positive, which is how solar reads.
                   *
                   * null -- absent, never 0 -- when this run carries no column
                   * for this device or the chart has not resolved a period. */
                  const rawDeviceDemand = sumDeviceAttributionForPeriod(
                    dailyDemand,
                    deviceName,
                    period.view,
                    period.selDate,
                    period.selMonth,
                    period.selYear,
                  );

                  /* normalizeMetrics' field names are historical and read
                   * backwards: `value` is the production energy and
                   * `totalEnergy` is the cost. Mapped onto the flow row's own
                   * names here rather than propagating the confusion. */
                  const source = {
                    maxPower,
                    totalProduction: value,
                    generatedValue,
                    /* The device's Production Cost, under the name the flow
                     * rows use. It is the second half of the Fuel row's
                     * derivation -- fuel = generated value less production
                     * cost -- and without it a fuelled generator's own card is
                     * the one place the fuel never appears, which is the card
                     * that exists to ask whether it earned its fuel. Same
                     * figure `deviceCardTotal` is handed just below. */
                    totalCost: totalEnergy,
                    deviceDemand:
                      rawDeviceDemand === null
                        ? null
                        : rawDeviceDemand.toFixed(2),
                  };
                  const flowRows = buildProductionFlowRows(source, demandData, {
                    perDevice: true,
                  });

                  /* The card's NET: the engine's Production Cost for this
                   * device plus this device's demand attribution. */
                  const { total, includesDemand } = deviceCardTotal(
                    totalEnergy,
                    source,
                    demandData,
                  );

                  return (
                    <div key={cardKey} className="card production-card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="d-flex align-items-center">
                            <h5
                              className="card-title mb-0 device-name-production"
                              title={deviceName.length > 25 ? deviceName : ""}
                            >
                              {deviceName.length > 25
                                ? `${deviceName.substring(0, 25)}...`
                                : deviceName}
                            </h5>
                            <img
                              src={isConnected ? connectedIcon : disconnectedIcon}
                              alt={isConnected ? "Connected" : "Disconnected"}
                              className="connection-icon ms-2"
                            />
                          </div>
                          <div className="box-transfer">
                            <span className="period-production">
                              {view == "All Time View"
                                ? "All Time View"
                                : `${view}: ${selectedPeriod}`}
                            </span>
                          </div>
                        </div>

                        <CardFlowRows
                          rows={flowRows}
                          total={total}
                          totalIncludesDemand={includesDemand}
                          testIdPrefix={`production-device-${deviceIndex}`}
                          showColumnHeaders
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <ProductionChart
        csvData={timeSeriesFileName}
        proddeviceNames={proddeviceNames}
        onLoadingStateChange={handleLoadingStateChange}
      />
    </div>
  );
}
